/**
 * ContentPlan.jsx
 * The month, as a calendar.
 *
 * Every date on this grid was computed in the Worker, not chosen by a model —
 * see services/copilot.js for why. That also means the plan is recomputed on
 * every read, so a month opened on the 20th shows the slots that are left
 * rather than the ones that have gone by.
 *
 * Before the month is generated these are intentions. Afterwards the drafts
 * are the truth, and the cell links to the real post.
 *
 * Dependencies: components/Icons.jsx, utils/time.js
 */

import { Calendar, ChevronRight } from '../Icons.jsx';
import { PLATFORMS, platformList, snippet } from '../dashboard/helpers.js';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Short names for the calendar chips. "Single post" wraps to two lines in a
 * cell this size and pushes the title out of view; the full label is still in
 * the chip's tooltip and on the idea list.
 */
const SHORT = { post: 'Post', carousel: 'Carousel', story: 'Story', link: 'Link' };

/** Monday-first weekday index for a 'YYYY-MM-DD'. */
const weekdayOf = (date) => (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;

const daysIn = (month) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

/** The month shifted by n, as 'YYYY-MM'. */
export function shiftMonth(month, n) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** 'YYYY-MM-DDTHH:MM:SSZ' → the London date and time it lands on. */
function londonParts(iso) {
  if (!iso) return null;
  const at = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(at.getTime())) return null;

  // en-CA gives YYYY-MM-DD, which is the only format worth sorting on.
  const date = at.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
  const time = at.toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
  });
  return { date, time };
}

const STATUS = {
  published: { label: 'Published', className: 'is-published' },
  scheduled: { label: 'Scheduled', className: 'is-scheduled' },
  publishing: { label: 'Publishing', className: 'is-scheduled' },
  failed: { label: 'Failed', className: 'is-failed' },
  draft: { label: 'Draft', className: 'is-draft' },
  approved: { label: 'Approved', className: 'is-draft' },
};

/**
 * What the month actually contains, in one line.
 *
 * Two different things can be in a cell — what Copilot proposes and what is
 * already there — and a heading that counted only the first is what made this
 * screen look like it had lost your posts.
 */
function describe({ plan, posts, existing, generatedIds }) {
  const already = existing.filter((p) => !generatedIds.has(p.id)).length;
  const alreadyText = already === 1 ? '1 post already in this month' : `${already} posts already in this month`;

  if (plan.length === 0) {
    return already
      ? `${alreadyText}. Generate a strategy and Copilot plans around them.`
      : 'Generate a strategy and the month fills in.';
  }

  const mine =
    posts.length > 0
      ? `${posts.length} drafts written by Copilot`
      : `${plan.length} proposed by Copilot, nothing written yet`;

  return already ? `${mine}, and ${alreadyText}.` : `${mine}.`;
}

export default function ContentPlan({
  month,
  monthLabel,
  plan = [],
  posts = [],
  existing = [],
  formats = {},
  onMonth,
  onShowIdeas,
  navigate,
}) {
  const total = daysIn(month);
  const lead = weekdayOf(`${month}-01`);

  // Once generated, the drafts are what exists — so the cell opens the real
  // post rather than an intention that has already been acted on.
  const postByTitle = new Map(posts.map((p) => [p.notes, p]));
  const generatedIds = new Set(posts.map((p) => p.id));

  const byDate = new Map();
  const add = (date, entry) => {
    const list = byDate.get(date) ?? [];
    list.push(entry);
    byDate.set(date, list);
  };

  // What is already in the month goes on first — scheduled, published and
  // drafted posts, whoever put them there. Copilot is not the only thing that
  // fills a calendar, and a month that showed only its own ideas looked empty
  // when it was not.
  for (const post of existing) {
    // A post this strategy generated is already represented by its idea; two
    // chips for one post would double-count the month.
    if (generatedIds.has(post.id)) continue;

    const when = londonParts(post.at);
    if (!when) continue;
    add(when.date, { kind: 'existing', post, time: when.time });
  }

  for (const item of plan) {
    add(item.slot.date, { kind: 'planned', item, time: item.slot.time });
  }

  // Within a day, in clock order, so a cell reads top to bottom as the day runs.
  for (const list of byDate.values()) list.sort((a, b) => a.time.localeCompare(b.time));

  const cells = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: total }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`),
  ];

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Calendar size={21} />
        </span>
        <div className="card__head-text">
          <h2>30-day content plan</h2>
          <p>{describe({ plan, posts, existing, generatedIds })}</p>
        </div>
        <div className="card__head-end">
          <div className="month-nav">
            <button
              className="icon-btn"
              onClick={() => onMonth(shiftMonth(month, -1))}
              aria-label="Previous month"
            >
              <span className="month-nav__back">
                <ChevronRight size={16} />
              </span>
            </button>
            <span className="month-nav__label">{monthLabel}</span>
            <button
              className="icon-btn"
              onClick={() => onMonth(shiftMonth(month, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <button className="btn btn--sm" onClick={() => navigate('/calendar')}>
            <Calendar size={15} /> View calendar
          </button>
        </div>
      </div>

      <div className="plan">
        <div className="plan__head">
          {WEEKDAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className="plan__grid">
          {cells.map((date, i) => {
            if (!date) return <div className="plan__cell plan__cell--blank" key={`pad-${i}`} />;

            const items = byDate.get(date) ?? [];
            return (
              <div className={`plan__cell${items.length ? ' has-posts' : ''}`} key={date}>
                <span className="plan__day">{Number(date.slice(-2))}</span>

                {items.map((entry, n) => {
                  // Something that already exists. It is drawn solid and opens
                  // the real post, because it is a fact rather than a proposal.
                  if (entry.kind === 'existing') {
                    const { post } = entry;
                    const state = STATUS[post.status] ?? STATUS.draft;
                    const first = platformList(post.platforms)[0];
                    const Mark = PLATFORMS[first]?.Icon;
                    const label = snippet(post.caption, 40) || post.notes || 'Untitled post';

                    return (
                      <button
                        className={`plan__post plan__post--existing ${state.className}`}
                        key={`${date}-${n}`}
                        type="button"
                        title={`${entry.time} · ${state.label} · ${label}`}
                        onClick={() => navigate(`/create?post=${post.id}`)}
                      >
                        <span className="plan__post-top">
                          {Mark && <Mark size={11} />}
                          <span className="plan__format">{state.label}</span>
                        </span>
                        <span className="plan__post-title">{label}</span>
                      </button>
                    );
                  }

                  const { item } = entry;
                  const post = postByTitle.get(item.title);
                  const platform = PLATFORMS[item.platforms?.[0]];
                  const Mark = platform?.Icon;

                  return (
                    <button
                      className={`plan__post plan__post--${item.format}`}
                      key={`${date}-${n}`}
                      type="button"
                      title={`${entry.time} · ${formats[item.format] ?? item.format} · ${item.title}${post ? '' : ' (not written yet)'}`}
                      // Before the month is written there is no post to open,
                      // so the idea list is the honest destination.
                      onClick={() =>
                        post ? navigate(`/create?post=${post.id}`) : onShowIdeas()
                      }
                    >
                      <span className="plan__post-top">
                        {Mark && <Mark size={11} />}
                        <span className="plan__format">
                          {SHORT[item.format] ?? item.format}
                        </span>
                      </span>
                      <span className="plan__post-title">{item.title}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <ul className="plan__legend">
        <li>
          <span className="plan__key plan__key--planned" /> Proposed by Copilot
        </li>
        <li>
          <span className="plan__key plan__key--scheduled" /> Already scheduled
        </li>
        <li>
          <span className="plan__key plan__key--published" /> Published
        </li>
        <li>
          <span className="plan__key plan__key--draft" /> Draft
        </li>
      </ul>

      {plan.length > 0 && posts.length === 0 && (
        <p className="card__footnote">
          Proposed slots are intentions, not schedules. Generating the month writes the captions
          and puts each one in drafts; nothing goes out until you approve it. Copilot routes
          around anything already in the month, so it never double-books a slot.
        </p>
      )}
    </section>
  );
}
